/**
 * Dependency column settings — the three modes and the connected boards.
 *
 * `dependency_mode` ∈ `flexible` | `strict` | `no_action` `[09§A.7 dependency]`; the one
 * column on this account stores `null`, i.e. monday's own default. The three could **not** be
 * measured on the sandbox (the column has no successor dates to watch move), so the wording
 * here is the documented name plus what the worker actually does, and the gap is q337.
 */
import { DEPENDENCY_MODES, dependencyMode, type DependencySettings } from '@elizmonday/shared';
import { useState } from 'react';
import type { ColumnSettingsProps } from '../types';

const EXPLANATION: Readonly<Record<string, string>> = {
  flexible: 'Moving a predecessor shifts this item’s dates by the same number of days.',
  strict: 'This item can never start before its predecessors finish; its dates are pushed.',
  no_action: 'Dates are never moved automatically.',
};

export function DependencySettingsDialog({ settings, onChange, onCommit, onCancel }: ColumnSettingsProps<DependencySettings>) {
  // Round 10i: `column.settings` is `null` on this account's only dependency column.
  const [draft, setDraft] = useState<DependencySettings>(() => ({ ...(settings ?? {}) }));
  const update = (next: DependencySettings): void => {
    setDraft(next);
    onChange(next);
  };
  return (
    <div className="ez-relation-settings" data-testid="dependency-settings">
      <h2 className="ez-relation-settings__title">Dependency column settings</h2>
      <h3 className="ez-relation-settings__section">When a predecessor moves</h3>
      {DEPENDENCY_MODES.map((mode) => (
        <label key={mode} className="ez-relation-settings__row">
          <span>
            {mode}
            <span className="ez-two-field__hint">{EXPLANATION[mode]}</span>
          </span>
          <input
            type="radio"
            name="dependency-mode"
            data-testid={`dependency-mode-${mode}`}
            checked={dependencyMode(draft) === mode}
            onChange={() => update({ ...draft, dependency_mode: mode })}
          />
        </label>
      ))}
      <label className="ez-relation-settings__row">
        <span>Allow linking to multiple items</span>
        <input
          type="checkbox"
          role="switch"
          data-testid="dependency-allow-multiple"
          checked={draft.allowMultipleItems !== false}
          onChange={(event) => update({ ...draft, allowMultipleItems: event.target.checked })}
        />
      </label>
      <div className="ez-relation-settings__footer">
        <button type="button" className="ez-picker__row" data-testid="settings-cancel" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="ez-formula-builder__primary"
          data-testid="dependency-save"
          onClick={() => onCommit(draft)}
        >
          Save
        </button>
      </div>
    </div>
  );
}
